/**
 * Valid call-agent emails — current org users plus the signed-in user, the
 * selected admin (shared access), and the impersonated user. Saved agent
 * lists in Firestore are raw email arrays that nothing prunes when a user
 * is removed from the org, so UI consumers filter through this set before
 * counting, displaying, or re-saving agents.
 *
 * Org membership is only authoritative when the fetch succeeded AND the
 * 200-doc query window cannot have truncated it (the transfer pre-validation
 * in call-store treats membership as advisory for the same reason). In any
 * doubtful state the hook returns null and callers keep the raw list —
 * showing a stale agent is recoverable, silently dropping a real one on
 * Save is not.
 */

import { useEffect, useMemo } from 'react';
import { useAppUsersStore } from '@/stores/app-users-store';
import { useAuthStore } from '@/stores/auth-store';

/** Mirrors the limit(200) in loadOrgUsers — at 200 docs the list may be truncated. */
const ORG_USERS_QUERY_LIMIT = 200;

/** The impersonation flow can fall back to storing a uid in the email field. */
const isEmail = (v: string | null | undefined): v is string => !!v && v.includes('@');

/** Returns null whenever the valid set cannot be trusted — callers must fall back to the raw list. */
export function useValidAgentEmails(): Set<string> | null {
  const orgUsers = useAppUsersStore((s) => s.orgUsers);
  const loaded = useAppUsersStore((s) => s.loaded);
  const loading = useAppUsersStore((s) => s.loading);
  const loadFailed = useAppUsersStore((s) => s.orgUsersLoadFailed);
  const loadOrgUsers = useAppUsersStore((s) => s.loadOrgUsers);
  const currentUserEmail = useAuthStore((s) => s.user?.email);
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const selectedAdminUserEmail = useAuthStore((s) => s.selectedAdminUserEmail);
  const impersonatedUserId = useAuthStore((s) => s.impersonatedUserId);
  const impersonatedUserEmail = useAuthStore((s) => s.impersonatedUserEmail);

  useEffect(() => {
    if (!loaded) loadOrgUsers();
  }, [loaded, loadOrgUsers]);

  return useMemo(() => {
    // `loading` also covers the warm-cache window: a ≤24h IDB snapshot paints
    // with loaded=true while the Firestore refresh is still in flight — only
    // trust membership once the current pass has settled.
    if (!loaded || loading || loadFailed) return null;
    // An active shared-admin or impersonation session whose email is unknown
    // (warm-device restores keep only the uid, and the impersonation flow can
    // store a uid in the email field) would be wrongly excluded from the
    // set — don't filter at all in that state.
    if (selectedAdminUserId && !isEmail(selectedAdminUserEmail)) return null;
    if (impersonatedUserId && !isEmail(impersonatedUserEmail)) return null;

    const emails = Object.values(orgUsers)
      .map((u) => u.email)
      .filter(Boolean);
    if (emails.length >= ORG_USERS_QUERY_LIMIT) return null;

    const valid = new Set<string>(emails.map((e) => e.toLowerCase()));
    if (currentUserEmail) valid.add(currentUserEmail.toLowerCase());
    if (selectedAdminUserEmail) valid.add(selectedAdminUserEmail.toLowerCase());
    if (impersonatedUserEmail) valid.add(impersonatedUserEmail.toLowerCase());
    return valid;
  }, [
    loaded,
    loading,
    loadFailed,
    orgUsers,
    currentUserEmail,
    selectedAdminUserId,
    selectedAdminUserEmail,
    impersonatedUserId,
    impersonatedUserEmail,
  ]);
}

/** Case-insensitive identity: two spellings of one address are one agent. */
export function isSameAgentEmail(a: unknown, b: unknown): boolean {
  return (
    typeof a === 'string' && typeof b === 'string' && a.toLowerCase() === b.toLowerCase()
  );
}

/**
 * Drop emails that are not valid agents. While the set is untrusted the raw
 * list passes through untouched — a Save must never lose anything on doubtful
 * data. When trusted, non-string entries (ring-group docs are cast from
 * Firestore unvalidated) and case-variant duplicates are dropped — two
 * spellings of one person must count, render, and dial once. Position of the
 * first occurrence is kept (sequential ring order), but the LAST spelling
 * wins: every writer appends the fresher org-record casing after a stale one,
 * so the last is the best proxy for the identity a device registered under.
 */
export function filterValidAgents(agents: string[], valid: Set<string> | null): string[] {
  if (!valid) return agents;
  const out: string[] = [];
  const posByKey = new Map<string, number>();
  for (const email of agents) {
    if (typeof email !== 'string') continue;
    const key = email.toLowerCase();
    if (!valid.has(key)) continue;
    const at = posByKey.get(key);
    if (at !== undefined) {
      out[at] = email;
      continue;
    }
    posByKey.set(key, out.length);
    out.push(email);
  }
  return out;
}
